import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProjectData } from "../slices/projectSlice";
import type { AppDispatch, RootState } from "../store";

export const useProjectMembers = () => {
  const dispatch = useDispatch<AppDispatch>();
  const lastFetchedProjectId = useRef<string | null>(null);
  const isFetchingRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);

  const { currentProject } = useSelector((state: RootState) => state.auth);
  const { isLoading, error } = useSelector((state: RootState) => state.project);

  const fetchProjectMembersData = useCallback(async () => {
    if (!currentProject?.id || isFetchingRef.current || !mountedRef.current) {
      return;
    }

    // 既に現在のプロジェクトのデータを取得済みの場合はスキップ
    if (lastFetchedProjectId.current === currentProject.id) {
      return;
    }

    // projectMembersにデータがある場合でも、lastFetchedProjectIdが一致しない場合は
    // 別プロジェクトのキャッシュデータの可能性があるため、再取得する
    // （URL直接アクセス時にパーシストされた別プロジェクトのデータが残っている場合の対応）

    try {
      isFetchingRef.current = true;
      await dispatch(fetchProjectData()).unwrap();
      if (mountedRef.current) {
        lastFetchedProjectId.current = currentProject.id;
      }
    } catch (error) {
      console.error("Failed to fetch project members:", error);
      if (mountedRef.current) {
        lastFetchedProjectId.current = null;
      }
    } finally {
      if (mountedRef.current) {
        isFetchingRef.current = false;
      }
    }
  }, [currentProject?.id, dispatch]);

  useEffect(() => {
    fetchProjectMembersData();
  }, [fetchProjectMembersData]);

  useEffect(() => {
    if (currentProject?.id && lastFetchedProjectId.current !== currentProject.id) {
      lastFetchedProjectId.current = null;
      isFetchingRef.current = false;
    }
  }, [currentProject?.id]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return { isLoading, error };
};
